import { api } from '../transport/http'
import type { AgentRow, IdleAgent, TerminalRow } from '../transport/api'
import { fetchServers, type PairedServer } from '../servers/serverList'

/**
 * What every paired machine is running, in one list.
 *
 * `/agent/all` answers for ONE machine, which was the whole question while an
 * account had one — and the sidebar's grouping by `host` came from the topology
 * underneath that: a single edge with other machines MOUNTED into it, so a "host"
 * was a remote reachable through it.
 *
 * Machines are paired DEVICES now. Each holds its own link to the router and knows
 * nothing about the others, so nobody can answer "what is this account running"
 * except by asking each of them. The owner met this as a sidebar that emptied when
 * a second machine came online: the relay points at one machine at a time, a new
 * one was chosen, and the screen faithfully reported that it had no agents.
 *
 * ONE SLOW MACHINE MUST NOT BLANK THE LIST. A machine that is away, or refusing, or
 * simply not answering, comes back as its own failure and the others are still
 * shown — with `failed` naming which ones, so "this account has no agents there"
 * and "we could not ask" stay different sentences.
 */
export type Everywhere = {
  agents: AgentRow[]
  terminals: TerminalRow[]
  /**
   * EVERY machine that was asked, answered or not.
   *
   * Without this the screen can only show machines that happen to be running
   * something, because the only evidence a machine exists is an agent naming it —
   * and the owner saw exactly that: a second machine paired, live, and invisible,
   * because it had nothing running yet. "This machine has no agents" is information.
   * Its absence is indistinguishable from never having asked.
   */
  machines: { device_id: string; name: string; sibling_of?: string | null }[]
  /**
   * Machines that could not give an answer, by name and in their own words.
   *
   * Two different situations, deliberately together: one could not be reached, and
   * one answered that it cannot see what it was asked about. The screen needs the
   * same thing from both — a sentence instead of a blank — and keeping them apart
   * here would mean drawing the same blank for one of them.
   */
  failed: { device_id: string; name: string; detail: string }[]
}

/**
 * Ask every machine, but never more than a few at once.
 *
 * `Promise.all(list.map(...))` opens one request per machine SIMULTANEOUSLY, and two
 * fan-outs run together after a create — agents and idle-status — each of which may
 * resubmit a lost read. The owner's browser ran out of sockets doing it:
 * ERR_INSUFFICIENT_RESOURCES, dozens of them, which is the client exhausting itself
 * rather than anything on the far side failing. Chrome allows six connections per
 * host, so past six the rest queue in the browser and every one of them ages toward
 * its own deadline while waiting for a socket, not for an answer.
 *
 * Three at a time, in order, results in the original positions. A machine that is slow
 * delays the ones behind it — which is the honest trade: the alternative is a burst
 * that delays ALL of them and can fail outright.
 */
async function askEach<T>(
  servers: readonly PairedServer[],
  ask: (s: PairedServer) => Promise<T>,
  /**
   * Called with each machine's answer THE MOMENT it lands, before the others finish.
   *
   * Without it the whole sweep is one atomic result, and the caller cannot tell the
   * difference between "aowl answered in 40ms" and "aowl answered after nine other
   * machines timed out" — because it is not told either until all ten are done.
   */
  onResult?: (value: T) => void,
): Promise<T[]> {
  const LIMIT = 3
  const out = new Array<T>(servers.length)
  let next = 0
  const worker = async (): Promise<void> => {
    /*
     * NOT A POLL, and noHeartbeat.test.ts is right to make me say so: this is the
     * "unbounded loop containing an await" shape, which is exactly the pattern that
     * rule exists to catch. It terminates on a FIXED list — `next` only increases and
     * the loop returns the moment it passes the end — so it asks each machine once and
     * stops. Nothing here re-asks a question already answered; it is a work queue with
     * a concurrency lid, and the lid is the whole point.
     */
    for (;;) {
      const i = next++
      if (i >= servers.length) return
      out[i] = await ask(servers[i]!)
      onResult?.(out[i]!)
    }
  }
  await Promise.all(Array.from({ length: Math.min(LIMIT, servers.length) }, worker))
  return out
}

/**
 * ASK ONE MACHINE INSTEAD OF ALL OF THEM, when one is all the caller can use.
 *
 * Owner, 2026-08-23: 感覺第二個 Session 開啟時非常的 Laggy. Measured on a split before
 * changing anything — from the drop to the pane showing its conversation was 5.6s, over
 * 39 requests, of which the top five were this sweep hitting FOUR machines:
 *
 *   device 4e9bb4d9  14x  3625ms      /account/devices   5x  1599ms
 *   device e5283439   4x  1414ms      device e96d193b    5x  1276ms
 *
 * A pane exists to show ONE conversation and its URL names the machine that
 * conversation is on. It has no rail, no roster and no fleet view — every other machine
 * it asks is a round trip whose answer it will not render. The shell still sweeps
 * everything, because the shell is the thing that draws the fleet.
 *
 * Scoped, not cached: an answer from the wrong machine is not stale, it is irrelevant,
 * and a cache would still have paid for it once.
 */
/** One machine's share of the roster sweep, offered the instant it answers. */
export type RosterMachine = {
  device_id: string
  name: string
  /** The machine whose shared home this one is a node of (PairedServer.sibling_of). */
  sibling_of?: string | null
  agents: AgentRow[]
  terminals: TerminalRow[]
  error: string | null
}

/**
 * Fold one machine's roster share into the picture already on screen.
 *
 * Same contract as mergeIdleMachine, same trap: rows for OTHER machines are
 * carried through untouched, because a partial answer that replaced the whole
 * map would make nine machines' agents vanish for as long as the sweep takes.
 * Added for create/delete latency (owner, 2026-08-30: Create / Delete Agent 的
 * Latency 非常的高. 居然要等十幾秒) — the rail only repainted when the WHOLE
 * fleet sweep resolved, so a change on the one machine that acted waited for
 * every machine that did not.
 */
export function mergeEverywhere(prev: Everywhere | undefined, m: RosterMachine): Everywhere {
  return {
    agents: spliceMachine(prev?.agents ?? [], m.device_id, m.agents),
    terminals: spliceMachine(prev?.terminals ?? [], m.device_id, m.terminals),
    machines: spliceMachine(prev?.machines ?? [], m.device_id, [{ device_id: m.device_id, name: m.name, sibling_of: m.sibling_of ?? null }]),
    failed: spliceMachine(prev?.failed ?? [], m.device_id, m.error !== null ? [{ device_id: m.device_id, name: m.name, detail: m.error }] : []),
  }
}

/**
 * One machine's rows put back WHERE THEY WERE, the other machines' untouched and in their order; a machine not seen
 * before goes at the end.
 *
 * Owner, 2026-11-04: 功能變多了之後, 現在 frontend app / UI 有時候會變得很卡. Taking a machine's rows out and appending its
 * new ones moved that machine to the end on every answer, so the list was never equal to the one before it — react-query
 * keeps the old data (and nothing re-renders) only when the new is deep-equal, and an answer that changed nothing still
 * re-rendered the app and the rail, once per machine per sweep.
 */
export function spliceMachine<T extends { device_id?: string | null | undefined }>(prev: readonly T[], device: string, rows: readonly T[]): T[] {
  const out: T[] = []
  let placed = false
  for (const r of prev) {
    if (r.device_id !== device) out.push(r)
    else if (!placed) {
      out.push(...rows)
      placed = true
    }
  }
  if (!placed) out.push(...rows)
  return out
}

export async function getAgentsEverywhere(
  onlyDevice?: string | null,
  /**
   * Incremental delivery, the same doors the idle sweep grew for the busy dot:
   * `first` moves one machine to the head of the queue, `onMachine` hands over each
   * machine's share as it lands. Added for the project-chat attach sheet (owner,
   * 2026-08-29: 等待 Agent list 的 Latency 非常的高! 我等了十幾秒) — the third
   * surface paying the whole-fleet lump for a question one machine answers fast.
   */
  opts?: { first?: string | null; onMachine?: (m: RosterMachine) => void },
): Promise<Everywhere> {
  const list = await fetchServers()
  if (list.kind !== 'servers') {
    // No machines, or the router could not say. Either way there is nothing to ask
    // — and an empty list here is the truth, not a swallowed error: `fetchServers`
    // has already reported what it knows.
    return { agents: [], terminals: [], machines: [], failed: [] }
  }

  /*
   * If the caller named a machine and we have it, that is the whole list. Naming one we
   * do NOT have falls through to the full sweep rather than answering nothing — the
   * device list can lag a fresh pairing, and an empty screen is worse than a slow one.
   */
  const scoped = onlyDevice ? list.servers.filter((s) => s.server_id === onlyDevice) : []
  const servers = firstOfAll(scoped.length ? scoped : list.servers, opts?.first)

  const asked = await askEach(
    servers,
    async (s: PairedServer) => {
      try {
        const got = await api.get<{
          agents: AgentRow[]
          terminals?: TerminalRow[]
          /**
           * The machine answering that it cannot answer.
           *
           * Present only when something is wrong with the machine's own setup —
           * today, `tmux is not on this machine's PATH`. A 200 with an empty list
           * and a 200 with an empty list AND this field are different facts, and
           * the day this was written was spent unable to tell them apart.
           */
          error?: string
          /**
           * avengents-server's additive contract (6660f76): absent means the listing
           * ran; `{listed: false, detail}` means the machine COULD NOT BE ASKED and
           * says why — today, "tmux is not installed on the server", which is macOS
           * default PATH. Their reason for keeping it absent-by-default: a field that
           * is always present teaches a client to ignore it.
           */
          local?: { listed: boolean; detail?: string }
        }>('/agent/all', s.server_id)
        return { server: s, got, error: null as string | null }
      } catch (e) {
        return { server: s, got: null, error: e instanceof Error ? e.message : String(e) }
      }
    },
    (r) =>
      opts?.onMachine?.({
        device_id: r.server.server_id,
        name: r.server.name,
        sibling_of: r.server.sibling_of ?? null,
        agents: (r.got?.agents ?? []).map((a) => ({
          ...a,
          device_id: r.server.server_id,
          device_name: r.server.name,
        })),
        terminals: (r.got?.terminals ?? []).map((t) => ({
          ...t,
          device_id: r.server.server_id,
          device_name: r.server.name,
        })),
        error: r.error,
      }),
  )

  const agents: AgentRow[] = []
  const terminals: TerminalRow[] = []
  const failed: Everywhere['failed'] = []
  // What was actually ASKED: otherwise "no agents on this machine yet" is said
  // about machines nobody spoke to.
  const machines: Everywhere['machines'] = servers.map((s) => ({
    device_id: s.server_id,
    name: s.name,
    sibling_of: s.sibling_of ?? null,
  }))

  for (const { server, got, error } of asked) {
    if (!got) {
      failed.push({ device_id: server.server_id, name: server.name, detail: error ?? '' })
      continue
    }
    if (got.error) {
      // ANSWERED, and the answer is that it cannot see. Recorded beside the
      // machines we could not reach because the screen needs the same thing from
      // both — the machine's own words — even though only one of them is a
      // transport failure.
      failed.push({ device_id: server.server_id, name: server.name, detail: got.error })
    } else if (got.local && got.local.listed === false) {
      /*
       * The Mac case, and the whole reason the field exists: the machine is
       * CONNECTED and EXECUTING (the BFF store showed its create collected the
       * same second), while `agents: []` alone is indistinguishable from a machine
       * that genuinely runs nothing. Their roster used to drop this reason one
       * line after computing it, and this screen drew "no agents on this machine
       * yet" about a machine that could not be asked.
       */
      failed.push({
        device_id: server.server_id,
        name: server.name,
        detail: got.local.detail ?? 'this machine could not list its sessions',
      })
    }
    // STAMPED HERE, because this is the only place that holds both halves: the
    // machine's answer, and the account's name for the machine that gave it.
    for (const a of got.agents ?? []) {
      agents.push({ ...a, device_id: server.server_id, device_name: server.name })
    }
    /*
     * TERMINALS GET THE SAME STAMP, and this line is a fix for a bug that lived two
     * lines under its own explanation. The comment above says "STAMPED HERE, because
     * this is the only place that holds both halves" — and the terminal loop pushed
     * rows through unstamped. A terminal created on the Mac appeared in the sidebar,
     * carried no device, and clicking it left the relay pointed wherever it already
     * was: openAgent's `if (a.device_id) setRelayTarget(...)` fell through, the
     * terminal opened on aowl, `tmux attach` found no such session, and the owner got
     * a plain shell in the wrong machine's home directory — while the create itself
     * had landed on the right machine and reported done. Verified against the BFF
     * store: CMD DEV#10102c9f agent.create test-agent1, state=done.
     */
    for (const t of got.terminals ?? []) {
      terminals.push({ ...t, device_id: server.server_id, device_name: server.name })
    }
  }

  return { agents, terminals, machines, failed }
}

/**
 * What every paired machine's agents are DOING — the second half of the same idea.
 *
 * `/agent/idle_status` answers for one machine, and this client asked it without
 * naming one, so it went to whichever machine the relay happened to be pointed at.
 * The roster above already asks all of them, so the two disagreed in a way that read
 * as missing data rather than as a missing question: the owner's second machine
 * listed its agents by name with no kind badge, no preview and no status dot —
 * because all three come from here.
 *
 * The comment on `api.get`'s `device` parameter states the rule this call was
 * missing: "the sidebar has to ask EVERY paired machine what it is running, and 'the
 * current one' is not an answer any of those questions has". That was written for the
 * roster; this is the sibling call it never reached.
 *
 * ONE SLOW MACHINE MUST NOT BLANK THE OTHERS, exactly as above. A machine that fails
 * contributes nothing to this tick and is named in `failed`; consumers keep the last
 * row they saw for its agents (useIdleStatus's `held`) rather than drawing them as
 * having gone quiet.
 */
export type IdleEverywhere = {
  agents: IdleAgent[]
  failed: { device_id: string; name: string; detail: string }[]
}

/**
 * One machine's share of the sweep, offered the instant that machine answers.
 *
 * `agents` is empty for a machine that could not be asked; `error` says which case it
 * is. Merging one of these is `mergeIdleMachine`, and the two exist together so a
 * partial answer is applied by REPLACING one machine's rows rather than by replacing
 * the whole picture with a fraction of it.
 */
export type IdleMachine = {
  device_id: string
  name: string
  agents: IdleAgent[]
  error: string | null
}

/**
 * Fold one machine's answer into the picture the screen is already showing.
 *
 * Rows for OTHER machines are carried through untouched. That is the whole point: a
 * partial sweep must not make nine machines look absent — `presenceFor` reads an
 * absent row as `off`, so replacing the map with just the machine that answered would
 * turn every other agent's dot dark for as long as the sweep takes. This is the same
 * hazard `held` exists for, met earlier and answered properly instead of ridden out.
 */
export function mergeIdleMachine(
  prev: IdleEverywhere | undefined,
  m: IdleMachine,
): IdleEverywhere {
  return {
    agents: spliceMachine(prev?.agents ?? [], m.device_id, m.agents.map((a) => ({ ...a, device_id: m.device_id, device_name: m.name }))),
    failed: spliceMachine(prev?.failed ?? [], m.device_id, m.error !== null ? [{ device_id: m.device_id, name: m.name, detail: m.error }] : []),
  }
}

/**
 * Put the machine the person is looking at at the FRONT of the sweep.
 *
 * With ten machines and three workers, whether the one on screen is asked first or
 * last is otherwise luck of the ordering — and last means its answer arrives after
 * three waves of machines nobody is watching. Pure, so the ordering is testable.
 */
export function firstOfAll<T extends { server_id: string }>(
  servers: readonly T[],
  first: string | null | undefined,
): T[] {
  if (!first) return [...servers]
  const i = servers.findIndex((s) => s.server_id === first)
  if (i <= 0) return [...servers]
  const out = [...servers]
  const [pick] = out.splice(i, 1)
  return pick ? [pick, ...out] : out
}

/**
 * What every agent on every paired machine is doing.
 *
 * ANSWERS ARRIVE ONE MACHINE AT A TIME, and `onMachine` is why. Measured 2026-08-29
 * against the owner's report (Agent 忙碌狀態的偵測感覺非常遲緩 […] 等過十秒), with the
 * pane, the edge and the rendered dot on one timeline:
 *
 *   t=3.7s   the pane's own machine says `active`
 *   t=13.8s  the dot lights                       ← 10.1s late
 *   t=23.7s  that machine says `idle`
 *   t=36.9s  the dot goes out                     ← 13.2s late
 *
 * The edge was never the slow half — it answered in about a second. The account has
 * TEN paired machines, and this function used to return only when the LAST of them
 * finished. A machine that is off costs a 4s relay deadline, a probe wait and a 4s
 * retry, so the machine being watched — which answers in about 40ms — had its answer
 * held until several machines nobody was looking at had finished timing out.
 *
 * So the sweep still asks everyone, and still costs what it costs; what changed is
 * that each answer is published as it lands instead of at the end.
 */
export async function getIdleEverywhere(opts?: {
  /** Ask this machine first — the one whose agents are on screen. */
  first?: string | null
  /** Called per machine, in arrival order, with that machine's share. */
  onMachine?: (m: IdleMachine) => void
  /**
   * Ask ONLY this machine — the idle_changed push names the machine that
   * flipped, and re-sweeping nine bystanders to hear about one is the lump
   * this family of fixes exists to kill. Unknown id falls through to the full
   * sweep, the same forgiving direction the roster's scoping takes.
   */
  only?: string | null
}): Promise<IdleEverywhere> {
  const list = await fetchServers()
  if (list.kind !== 'servers') return { agents: [], failed: [] }
  if (opts?.only) {
    const scoped = list.servers.filter((s) => s.server_id === opts.only)
    if (scoped.length) list.servers = scoped
  }

  const asked = await askEach(
    firstOfAll(list.servers, opts?.first),
    async (s: PairedServer) => {
      try {
        const got = await api.get<{ agents: IdleAgent[] }>('/agent/idle_status', s.server_id)
        return { server: s, got, error: null as string | null }
      } catch (e) {
        return { server: s, got: null, error: e instanceof Error ? e.message : String(e) }
      }
    },
    (r) =>
      opts?.onMachine?.({
        device_id: r.server.server_id,
        name: r.server.name,
        agents: r.got?.agents ?? [],
        error: r.error,
      }),
  )

  const agents: IdleAgent[] = []
  const failed: IdleEverywhere['failed'] = []
  for (const { server, got, error } of asked) {
    if (!got) {
      failed.push({ device_id: server.server_id, name: server.name, detail: error ?? '' })
      continue
    }
    /*
     * STAMPED with the machine that answered, because `host` cannot identify one.
     *
     * Every machine calls its own agents `host: null` — that field describes a remote
     * MOUNTED into an edge, which is the previous topology. So two machines each
     * running a session named `dev` produce the same `host + session` key and
     * whichever answered last wins: one agent's preview and status rendered under the
     * other's name, with nothing on screen to suggest it. See `idleKey`.
     */
    for (const a of got.agents ?? []) {
      agents.push({ ...a, device_id: server.server_id, device_name: server.name })
    }
  }
  return { agents, failed }
}

/**
 * The identity of one agent's status row, everywhere it is looked up.
 *
 * Built by hand in five separate places before this, all of them `host + session` —
 * which is not unique across machines (see the stamp above), and could not be fixed
 * in one place because there was no one place. The device leads, which is also how
 * the rail groups.
 */
export function idleKey(a: {
  device_id?: string | undefined
  host?: string | null
  session: string
}): string {
  return [a.device_id ?? '', a.host ?? '', a.session].join('\u0000')
}
